import type { AlignType } from "rc-trigger/lib/interface";
import { observer } from "mobx-react-lite";
import React, { ReactElement, useState } from "react";
import { Box, BoxProps, Center, Flex, Spacer, Text, chakra } from "@chakra-ui/react";

import { langStore, translate } from "@noli/business";

import "./TextTrigger.scss";
import { genBlock } from "@/utils/bem";
import { themeStore } from "@/stores/theme";
import { ReactComponent as ArrowSvg } from "@/assets/icons/arrow-down.svg";
import Icon from "@/components/Icon";
import { Popover, PopoverProps } from "@noli/noli-ui";

const block = genBlock("c-text-trigger");

export type TextTriggerProps = {
  panelTitle: string;
  storeValue: string | string[];
  setStoreValue: (value: any) => void;
  popupGenerator: (value: any, setValue: (value: any) => void) => ReactElement;
  popupAlign?: AlignType;
  popupClassName?: string;
  textH?: number;
  valueFormatter?: (value: string | string[]) => React.ReactNode;
  onVisibleChange?: (visible: boolean) => void;
} & BoxProps &
  Partial<Pick<PopoverProps, "safeDirection" | "safeDistance" | "smartAlign">>;
function TextTrigger({
  panelTitle,
  storeValue,
  setStoreValue,
  popupGenerator,
  popupAlign,
  popupClassName,
  textH,
  safeDirection,
  safeDistance,
  smartAlign,
  valueFormatter,
  onVisibleChange,
  ...others
}: TextTriggerProps) {
  const [visible, setVisible] = useState(false);

  const lang = langStore.tgt;
  const { titleColor } = themeStore.styles;

  textH ??= 60;

  function setValue(value: any): void {
    setStoreValue(value);
    setVisible(false);
  }

  return (
    <Center {...others}>
      <Box w="100%" h={`${textH}px`} cursor="pointer" userSelect="none">
        <Popover
          action={["click"]}
          popupVisible={visible}
          onPopupVisibleChange={(visible) => {
            setVisible(visible);
            onVisibleChange?.(visible);
          }}
          content={popupGenerator(storeValue, setValue)}
          safeDirection={safeDirection ?? "down"}
          safeDistance={safeDistance ?? 300}
          smartAlign={
            smartAlign ?? {
              points: ["bl", "br"],
              offset: [32, 5],
            }
          }
          popupClassName={popupClassName}
          popupAlign={
            popupAlign ?? {
              points: ["tl", "tr"],
              offset: [32, 5],
            }
          }
        >
          <Flex
            h={`${textH}px`}
            color={titleColor}
            fontSize="14px"
            align="center"
            _hover={{
              color: "#3fc9a8",
            }}
          >
            <Text>
              {panelTitle} :{" "}
              {valueFormatter ? (
                valueFormatter(storeValue)
              ) : (
                <chakra.span>
                  {typeof storeValue === "string" && translate(storeValue, lang)}
                </chakra.span>
              )}
            </Text>
            <Spacer />
            <Icon squared={false} className={block({ e: "arrow_icon" })} svg={ArrowSvg} />
          </Flex>
        </Popover>
      </Box>
    </Center>
  );
}

export default observer(TextTrigger);
